<template>
  <div class="app-main-inner ai-dashboard">
    <div class="dashboard-stats">
      <div
        v-for="item in statsCards"
        :key="item.label"
        class="section stat-card"
        :class="item.cardClass"
      >
        <div class="stat-card__label">{{ item.label }}</div>
        <div class="stat-card__value-row">
          <span class="stat-card__value">{{ item.value }}</span>
          <span class="stat-card__unit">笔</span>
        </div>
      </div>
    </div>

    <div class="section dashboard-table-section">
      <div class="dashboard-table-toolbar">
        <div class="dashboard-table-toolbar__main">
          <div class="dashboard-table-title">
            <i class="el-icon-s-order" />
            <span>最新对账任务</span>
          </div>

          <div class="dashboard-search">
            <el-input
              v-model="searchForm.taskNo"
              clearable
              size="mini"
              placeholder="任务编号"
              style="width: 140px"
              @keyup.enter.native="handleSearch"
            />
            <el-select
              v-model="searchForm.supplierId"
              filterable
              remote
              clearable
              size="mini"
              placeholder="供应商"
              :remote-method="remoteSupplierMethod"
              :loading="supplierLoading"
              @change="handleSearch"
            >
              <el-option
                v-for="item in supplierOptions"
                :key="item.supplierId"
                :label="item.supplierName"
                :value="item.supplierId"
              />
            </el-select>
            <el-select
              v-model="searchForm.billType"
              clearable
              size="mini"
              placeholder="账单类型"
              @change="handleSearch"
            >
              <el-option
                v-for="item in billTypeOptions"
                :key="item.code"
                :label="item.label"
                :value="item.code"
              />
            </el-select>
            <el-date-picker
              v-model="searchForm.billPeriod"
              type="daterange"
              clearable
              size="mini"
              value-format="yyyy-MM-dd"
              start-placeholder="周期开始日期起"
              end-placeholder="周期开始日期止"
              @change="handleSearch"
            />
            <el-button size="mini" @click="handleReset">重置</el-button>
          </div>
        </div>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="$emit('create-task')">
          新建对账任务
        </el-button>
      </div>

      <div class="dashboard-table-body">
        <el-table
          v-loading="loading"
          :data="tasks"
          border
          stripe
          size="mini"
          class="dashboard-table"
          header-cell-class-name="dashboard-table__header"
        >
          <el-table-column prop="taskNo" label="任务编号" min-width="130">
            <template slot-scope="{ row }">
              <el-button v-if="row.canView" type="text" size="mini" @click="openTask(row)">
                {{ row.taskNo }}
              </el-button>
              <span
                v-else
                class="task-no"
                :class="{ 'task-no--invalidated': row.isInvalidated }"
              >
                {{ row.taskNo }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="supplier" label="供应商" min-width="180" />
          <el-table-column prop="billType" label="账单类型" min-width="90" />
          <el-table-column prop="fileName" label="文件名" min-width="130" show-overflow-tooltip />
          <el-table-column prop="billingCycle" label="账单周期" min-width="90" />
          <el-table-column prop="uploadTime" label="上传时间" min-width="140" />
          <el-table-column prop="amount" label="账单金额" min-width="110" align="right" />
          <el-table-column label="状态" min-width="110" align="center">
            <template slot-scope="{ row }">
              <span class="status-pill" :class="row.statusClass">{{ row.statusText }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="190" align="center">
            <template slot-scope="{ row }">
              <el-button
                v-if="row.canView"
                type="text"
                size="mini"
                @click="openTask(row)"
              >
                查看详情
              </el-button>
              <el-button
                v-if="row.canInvalidate"
                type="text"
                size="mini"
                class="invalidate-button"
                :loading="invalidatingTaskUuid === row.taskUuid"
                @click="handleInvalidate(row)"
              >
                作废
              </el-button>
              <span v-if="!row.canView && !row.canInvalidate" class="action-disabled">-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="dashboard-pagination">
        <div class="dashboard-pagination__summary">{{ pageSummary }}</div>
        <el-pagination
          background
          small
          :current-page.sync="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size.sync="pageSize"
          layout="total, sizes, prev, pager, next"
          :total="total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { queryReconTaskPage, searchSuppliers, getReconMeta, invalidateReconTask, getReconDashboardStats } from '@/api/clearing/aiReconciliation'

// 账单类型 code → label（与后端 ReconBillType 枚举保持一致）
const BILL_TYPE_MAP = {
  FLEET: '干线费用',
  BOOKING: '订舱费用',
  CUSTOMS: '报关费用',
  FBA: '仓储费用',
  OVERSEAS_FLEET: '海外尾程',
  OTHER: '其他费用'
}

// 任务状态 code → { text, class }（与后端 ReconTaskStatus 枚举保持一致）
const STATUS_MAP = {
  CREATED: { text: '待解析', class: 'status-pill--gray' },
  PARSING: { text: '解析中', class: 'status-pill--blue' },
  PARSE_FAILED: { text: '解析失败', class: 'status-pill--red' },
  PARSED: { text: '解析完成', class: 'status-pill--lime' },
  MATCHING: { text: '匹配中', class: 'status-pill--blue' },
  MATCH_FAILED: { text: '匹配失败', class: 'status-pill--red' },
  REVIEWING: { text: '待复核', class: 'status-pill--orange' },
  COMPLETING: { text: '完成中', class: 'status-pill--blue' },
  COMPLETED: { text: '已完成', class: 'status-pill--green' },
  PARTIALLY_COMPLETED: { text: '部分完成', class: 'status-pill--lime' },
  INVALIDATED: { text: '已作废', class: 'status-pill--gray' }
}

const NON_VIEWABLE_STATUSES = ['CREATED', 'PARSING', 'INVALIDATED']
const INVALIDATABLE_STATUSES = ['CREATED', 'PARSE_FAILED', 'PARSED', 'REVIEWING']
function formatCycle(start) {
  if (!start) return ''
  return String(start).substring(0, 10) // yyyy-MM-dd
}

function formatAmount(val) {
  if (val == null || val === '') return '-'
  const num = Number(val)
  if (isNaN(num)) return '-'
  return '¥' + num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default {
  name: 'TabLogisticsBillImport',
  props: {
    refreshKey: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: false,
      taskRefreshing: false,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      tasks: [],
      invalidatingTaskUuid: '',
      searchForm: {
        supplierId: '',
        billType: '',
        taskNo: '',
        billPeriod: []
      },
      supplierOptions: [],
      supplierLoading: false,
      supplierTimer: null,
      autoRefreshTimer: null,
      billTypeOptions: [],
      // 首页统计卡片（由 fetchDashboardStats 从后端聚合接口填充）
      statsCards: [
        { label: '待处理对账', value: 0, cardClass: 'stat-card--blue' },
        { label: '已完成对账', value: 0, cardClass: 'stat-card--green' },
        { label: '争议中', value: 0, cardClass: 'stat-card--orange' },
        { label: '异常未匹配', value: 0, cardClass: 'stat-card--red' },
        { label: '系统无对应', value: 0, cardClass: 'stat-card--gray' }
      ]
    }
  },
  computed: {
    pageSummary() {
      const total = this.total
      if (total === 0) return '暂无数据'
      const start = (this.currentPage - 1) * this.pageSize + 1
      const end = Math.min(this.currentPage * this.pageSize, total)
      return `显示 ${start} 到 ${end} 条，共 ${total} 条`
    }
  },
  watch: {
    refreshKey(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1
        this.fetchTasks()
      }
    }
  },
  created() {
    this.loadMeta()
    this.remoteSupplierMethod('')
    this.fetchTasks()
    this.fetchDashboardStats()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    if (this.supplierTimer) {
      clearTimeout(this.supplierTimer)
      this.supplierTimer = null
    }
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer)
      this.autoRefreshTimer = null
    }
  },
  methods: {
    startAutoRefresh() {
      this.autoRefreshTimer = setInterval(() => {
        // 避免慢请求尚未结束时叠加新的列表请求。
        if (this.taskRefreshing) return
        this.fetchTasks(true)
        this.fetchDashboardStats()
      }, 10000)
    },
    fetchDashboardStats() {
      getReconDashboardStats()
        .then(res => {
          const data = (res && res.data) || {}
          this.statsCards[0].value = data.pending || 0
          this.statsCards[1].value = data.completed || 0
          this.statsCards[2].value = data.disputing || 0
          this.statsCards[3].value = data.exceptionUnmatched || 0
          this.statsCards[4].value = data.sourceOnly || 0
        })
        .catch(() => {})
    },
    loadMeta() {
      getReconMeta()
        .then(res => {
          const data = (res && res.data) || {}
          this.billTypeOptions = data.billTypes || []
        })
        .catch(() => {})
    },
    remoteSupplierMethod(query) {
      if (this.supplierTimer) {
        clearTimeout(this.supplierTimer)
      }
      this.supplierTimer = setTimeout(() => {
        this.supplierLoading = true
        searchSuppliers({ likeName: query || null, pageSize: 100 })
          .then(res => {
            this.supplierOptions = (res && res.data) || []
          })
          .catch(() => {
            this.supplierOptions = []
          })
          .finally(() => {
            this.supplierLoading = false
          })
      }, 300)
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchTasks()
    },
    handleReset() {
      this.searchForm.supplierId = ''
      this.searchForm.billType = ''
      this.searchForm.taskNo = ''
      this.searchForm.billPeriod = []
      this.currentPage = 1
      this.fetchTasks()
    },
    fetchTasks(silent = false) {
      if (this.taskRefreshing) return
      this.taskRefreshing = true
      if (!silent) {
        this.loading = true
      }
      queryReconTaskPage({
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        supplierId: this.searchForm.supplierId || undefined,
        billType: this.searchForm.billType || undefined,
        taskNo: this.searchForm.taskNo || undefined,
        billPeriodStart: (this.searchForm.billPeriod && this.searchForm.billPeriod[0]) || undefined,
        billPeriodEnd: (this.searchForm.billPeriod && this.searchForm.billPeriod[1]) || undefined
      })
        .then(res => {
          const data = (res && res.data) || {}
          this.total = data.total || 0
          this.tasks = (data.list || []).map(item => this.mapTask(item))
        })
        .catch(() => {
          this.tasks = []
          this.total = 0
        })
        .finally(() => {
          this.taskRefreshing = false
          if (!silent) {
            this.loading = false
          }
        })
    },
    mapTask(item) {
      const statusInfo = STATUS_MAP[item.status] || { text: item.status, class: 'status-pill--gray' }
      return {
        taskUuid: item.taskUuid,
        taskNo: item.taskNo,
        supplier: item.supplierName,
        billType: BILL_TYPE_MAP[item.billType] || item.billType,
        fileName: item.originalFileName,
        billingCycle: formatCycle(item.periodStart, item.periodEnd),
        uploadTime: item.createdTime,
        amount: formatAmount(item.cnyTotal),
        statusText: statusInfo.text,
        statusClass: statusInfo.class,
        isInvalidated: item.status === 'INVALIDATED',
        canView: !NON_VIEWABLE_STATUSES.includes(item.status),
        canInvalidate: INVALIDATABLE_STATUSES.includes(item.status)
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchTasks()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchTasks()
    },
    openTask(row) {
      if (!row.canView) {
        return
      }
      this.$emit('view-task', row)
    },
    handleInvalidate(row) {
      this.$prompt('作废后任务将变为只读，且不能恢复。请输入作废原因：', '作废对账任务', {
        confirmButtonText: '确认作废',
        cancelButtonText: '取消',
        inputPlaceholder: '请输入作废原因',
        inputValidator: value => value && value.trim() ? true : '作废原因不能为空',
        inputErrorMessage: '作废原因不能为空'
      }).then(({ value }) => {
        this.invalidatingTaskUuid = row.taskUuid
        return invalidateReconTask(row.taskUuid, value.trim())
          .then(() => {
            this.$message.success('作废成功')
            this.fetchTasks()
          })
          .finally(() => {
            this.invalidatingTaskUuid = ''
          })
      }).catch(() => {}) // 用户取消/关闭：静默；接口失败由 request 拦截器统一提示
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-dashboard {
  height: 100%;
  min-height: 0;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 20px;
}

.dashboard-stats {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 5px;
  margin-bottom: 5px;
}

.stat-card {
  min-height: 84px;
  justify-content: center;
  border-left: 4px solid transparent;
}

.stat-card--blue {
  border-left-color: #3b82f6;
}

.stat-card--green {
  border-left-color: #22c55e;
}

.stat-card--orange {
  border-left-color: #f97316;
}

.stat-card--red {
  border-left-color: #ef4444;
}

.stat-card__label {
  color: #606266;
  font-size: 13px;
}

.stat-card__value-row {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
}

.stat-card__value {
  font-size: 26px;
  line-height: 1;
  font-weight: 700;
  color: #303133;
}

.stat-card__unit {
  margin-left: 6px;
  color: #409eff;
  font-weight: 600;
}

.ai-dashboard .dashboard-table-section {
  flex: 1 1 auto;
  flex-shrink: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
}

.dashboard-table-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.dashboard-table-toolbar__main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.dashboard-search {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-table-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #303133;

  i {
    margin-right: 6px;
    color: #409eff;
    font-size: 14px;
  }
}

.dashboard-table-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.dashboard-table {
  width: 100%;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 0 10px;
  height: 24px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 24px;
}

.status-pill--green {
  color: #389e0d;
  background: #f6ffed;
}

.status-pill--blue {
  color: #2563eb;
  background: #eff6ff;
}

.status-pill--lime {
  color: #d97706;
  background: #ecfccb;
}

.status-pill--pink {
  color: #db2777;
  background: #fdf2f8;
}

.status-pill--gray {
  color: #606266;
  background: #f4f4f5;
}

.status-pill--red {
  color: #dc2626;
  background: #fef2f2;
}

.status-pill--orange {
  color: #ea580c;
  background: #fff7ed;
}

.action-disabled {
  color: #c0c4cc;
}

.task-no {
  color: #606266;
}

.task-no--invalidated {
  text-decoration: line-through;
}

.invalidate-button {
  color: #f56c6c;
}

.dashboard-pagination {
  flex-shrink: 0;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dashboard-pagination__summary {
  color: #606266;
}

@media (max-width: 1200px) {
  .dashboard-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-table-toolbar,
  .dashboard-pagination {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .dashboard-table-toolbar__main {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .dashboard-search {
    flex-wrap: wrap;
  }

  .dashboard-stats {
    grid-template-columns: 1fr;
  }
}
</style>
